import { FC, ReactNode, useEffect, useRef } from 'react';
import { Transition } from '@headlessui/react';
import CrossMark from 'components/Icons/CrossMark';
import useOnClickOutside from 'hooks/useOnClickOutside';
import OverlayPortal from './OverlayPortal';

interface UIModalProps {
  isVisible: boolean;
  // eslint-disable-next-line no-unused-vars
  onToggleVisibility?: (visibility: boolean) => void;
  modalCardClassName?: string;
  disableScrollLock?: boolean;
  closeable?: boolean;
  customCloseButton?: ReactNode;
}

const UIModal: FC<UIModalProps> = ({
  isVisible,
  children,
  onToggleVisibility,
  modalCardClassName = '',
  disableScrollLock = false,
  closeable = true,
  customCloseButton
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disableScrollLock) return;

    const rootEl = document.querySelector('html');

    if (!isVisible)
      return rootEl?.removeAttribute('data-fullscreen-overlay-visible');

    rootEl?.setAttribute('data-fullscreen-overlay-visible', 'true');

    return () => {
      if (!disableScrollLock) {
        rootEl?.removeAttribute('data-fullscreen-overlay-visible');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const contentElement = useRef<HTMLDivElement>(null);

  const hideOverlay = () => {
    if (!closeable || !isVisible || !onToggleVisibility) return;
    onToggleVisibility(false);
  };

  const onClickOutside = (event: MouseEvent | TouchEvent) => {
    const overlayPortal = document.querySelector('#overlay-portal');

    const target = event.target as HTMLDivElement;

    const isEventFromAnotherOverlay =
      target &&
      overlayPortal?.contains(target) &&
      !overlayRef.current?.contains(target);

    if (isEventFromAnotherOverlay) return;

    hideOverlay();
  };

  useOnClickOutside(contentElement, onClickOutside);

  return (
    <OverlayPortal>
      <div ref={overlayRef}>
        <Transition
          data-fullscreen-overlay
          appear={true}
          show={isVisible}
          className="fixed left-0 top-0 z-[999] flex h-full w-full items-center justify-center">
          <Transition.Child
            enter="transition-opacity ease-linear duration-[250ms]"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-[250ms]"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className="overlay-backdrop">
            <div className="overlay-backdrop absolute left-0 top-0 h-full w-full bg-black opacity-[0.65]" />
          </Transition.Child>
          <Transition.Child
            enter="transition ease-in-out duration-[350ms] transform"
            enterFrom="opacity-0 translate-y-[50px]"
            enterTo="translate-y-0 opacity-100"
            leave="transition ease-in-out duration-[350ms] transform"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="translate-y-[100px] opacity-0"
            className={`z-[1] h-full w-full overflow-auto ${customCloseButton ? 'flex items-end md:items-center justify-center' : 'flex items-center justify-center'}`}>
            <div className={`relative mx-auto w-full md:w-auto ${customCloseButton ? 'md:mb-[10px] md:pt-[60px]' : 'my-[10px]'}`}>
              <div className="hidden md:block">{customCloseButton}</div>
              <div
                className={`relative bg-white ${modalCardClassName}`}
                ref={contentElement}>
                <div className="md:hidden">{customCloseButton}</div>
                {closeable && !customCloseButton && (
                  <span
                    data-testid="modal-close-button"
                    onClick={hideOverlay}
                    className="absolute z-50 right-[16px] top-[16px] cursor-pointer text-brand-black100 hover:text-brand-orange">
                    <CrossMark width={18} height={18} fill="currentColor" />
                  </span>
                )}
                {children}
              </div>
            </div>
          </Transition.Child>
        </Transition>
      </div>
    </OverlayPortal>
  );
};

export default UIModal;
